import Vue from "vue";

const TTL_MS = 300000; // 5 minutes

export default {
    namespaced: true,

    state: {
        cache: {},       // { [key]: { data: HelpContent, fetchedAt: timestamp } }
        inflight: [],    // keys currently being fetched
        panelOpen: false,
        currentKey: null
    },

    getters: {
        // Returns undefined if not yet fetched, null if fetched but not found, or the HelpContent object.
        getByKey(state) {
            return function(key) {
                if (!Object.prototype.hasOwnProperty.call(state.cache, key)) return undefined;
                return state.cache[key].data;
            };
        }
    },

    mutations: {
        SET_CACHE(state, { key, data }) {
            Vue.set(state.cache, key, { data, fetchedAt: Date.now() });
        },
        SET_INFLIGHT(state, key) {
            state.inflight.push(key);
        },
        REMOVE_INFLIGHT(state, key) {
            state.inflight = state.inflight.filter(k => k !== key);
        },
        SET_PANEL(state, { open, key }) {
            state.panelOpen = open;
            state.currentKey = key;
        },
        INVALIDATE_CACHE(state, key) {
            if (Object.prototype.hasOwnProperty.call(state.cache, key)) {
                Vue.set(state.cache, key, { data: state.cache[key].data, fetchedAt: 0 });
            }
        }
    },

    actions: {
        async fetchByKey({ state, commit }, key) {
            const cached = state.cache[key];
            if (cached && Date.now() - cached.fetchedAt < TTL_MS) {
                return;
            }
            if (state.inflight.includes(key)) {
                return;
            }
            commit('SET_INFLIGHT', key);
            try {
                const response = await Vue.prototype.$http.get(`/help/${key}`);
                commit('SET_CACHE', { key, data: response.data });
            } catch (error) {
                // Cache null so components know the fetch completed with no result
                commit('SET_CACHE', { key, data: null });
                if (!error.response || error.response.status !== 404) {
                    console.error('[help] fetchByKey failed for key "' + key + '":', error);
                }
            } finally {
                commit('REMOVE_INFLIGHT', key);
            }
        },

        async openPanel({ commit, dispatch }, key) {
            await dispatch('fetchByKey', key);
            commit('SET_PANEL', { open: true, key });
        },

        closePanel({ commit }) {
            commit('SET_PANEL', { open: false, key: null });
        },

        prefetchPage({ dispatch }, routeName) {
            if (!routeName) {
                return;
            }
            dispatch('fetchByKey', routeName);
        },

        async saveHelp({ commit, dispatch }, { key, content }) {
            try {
                await Vue.prototype.$http.put(`/help/${key}`, content);
                commit('INVALIDATE_CACHE', key);
                await dispatch('fetchByKey', key);
                // Notify other windows (e.g. popup → main) to re-fetch this key
                localStorage.setItem('help:invalidate', JSON.stringify({ key: key, ts: Date.now() }));
            } catch (error) {
                console.error('[help] saveHelp failed for key "' + key + '":', error);
                throw error;
            }
        }
    }
};
